/**
 * AVM per-lead localStorage utility with timestamp-based expiry and entry cap.
 *
 * Migrated from cookies to localStorage to prevent "Request Entity Too Large"
 * errors caused by per-lead cookies accumulating and inflating HTTP headers.
 */

const ANALYTICAL_DATA_PREFIX = 'analytical_data_';
const ACTIVE_TAB_PREFIX = 'active_tab_';
const EXPLORER_FILTERS_PREFIX = 'explorer_filters_';
const MIGRATION_FLAG = 'avm_cookies_migrated';

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_ENTRIES_PER_PREFIX = 50;

const ALL_PREFIXES = [ANALYTICAL_DATA_PREFIX, ACTIVE_TAB_PREFIX, EXPLORER_FILTERS_PREFIX];

interface StoredEntry {
  value: string;
  timestamp: number;
}

// Accessing window.localStorage itself throws a SecurityError in iframes with
// third-party storage blocked (widget embeds), so the probe must be guarded.
const isBrowser = () => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function readEntry(key: string): string | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry: StoredEntry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return entry.value;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function writeEntry(key: string, value: string): void {
  if (!isBrowser()) return;
  try {
    const entry: StoredEntry = { value, timestamp: Date.now() };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (e) {
    console.warn('[avm-local-storage] Write error:', e);
  }
}

function removeEntry(key: string): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Public API — Analytical Data
// ---------------------------------------------------------------------------

export function getAnalyticalData(leadId: string): string | null {
  return readEntry(`${ANALYTICAL_DATA_PREFIX}${leadId}`);
}

export function setAnalyticalData(leadId: string, data: string): void {
  writeEntry(`${ANALYTICAL_DATA_PREFIX}${leadId}`, data);
}

export function getAnalyticalDataTimestamp(leadId: string): number | null {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(`${ANALYTICAL_DATA_PREFIX}${leadId}`);
    if (!raw) return null;
    const entry: StoredEntry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > MAX_AGE_MS) return null;
    return entry.timestamp;
  } catch {
    return null;
  }
}

export function removeAnalyticalData(leadId: string): void {
  removeEntry(`${ANALYTICAL_DATA_PREFIX}${leadId}`);
}

// ---------------------------------------------------------------------------
// Public API — Active Tab
// ---------------------------------------------------------------------------

export const COMPARABLES_TABS = ['standard', 'explorer', 'agency', 'references'] as const;
export type ComparablesTab = (typeof COMPARABLES_TABS)[number];

export function isComparablesTab(value: string | null): value is ComparablesTab {
  return (COMPARABLES_TABS as readonly (string | null)[]).includes(value);
}

export function getActiveTab(leadId: string): string | null {
  return readEntry(`${ACTIVE_TAB_PREFIX}${leadId}`);
}

export function setActiveTab(leadId: string, tab: string): void {
  writeEntry(`${ACTIVE_TAB_PREFIX}${leadId}`, tab);
}

// ---------------------------------------------------------------------------
// Public API — Explorer Filters
// ---------------------------------------------------------------------------

export function getExplorerFilters(leadId: string): string | null {
  return readEntry(`${EXPLORER_FILTERS_PREFIX}${leadId}`);
}

export function setExplorerFilters(leadId: string, filters: string): void {
  writeEntry(`${EXPLORER_FILTERS_PREFIX}${leadId}`, filters);
}

// ---------------------------------------------------------------------------
// Cleanup — remove expired entries & enforce per-prefix cap
// ---------------------------------------------------------------------------

export function cleanupAvmStorage(): void {
  if (!isBrowser()) return;

  try {
    for (const prefix of ALL_PREFIXES) {
      const entries: { key: string; timestamp: number }[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;

        try {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          const parsed: StoredEntry = JSON.parse(raw);

          // Remove expired
          if (Date.now() - parsed.timestamp > MAX_AGE_MS) {
            localStorage.removeItem(key);
            continue;
          }

          entries.push({ key, timestamp: parsed.timestamp });
        } catch {
          localStorage.removeItem(key);
        }
      }

      // Enforce cap — evict oldest if over limit
      if (entries.length > MAX_ENTRIES_PER_PREFIX) {
        entries.sort((a, b) => a.timestamp - b.timestamp);
        const toEvict = entries.slice(0, entries.length - MAX_ENTRIES_PER_PREFIX);
        toEvict.forEach(({ key }) => localStorage.removeItem(key));
      }
    }
  } catch (e) {
    console.warn('[avm-local-storage] Cleanup error:', e);
  }
}

// ---------------------------------------------------------------------------
// One-time migration from cookies → localStorage
// ---------------------------------------------------------------------------

export function migrateAvmCookiesToLocalStorage(): void {
  if (!isBrowser()) return;

  try {
    if (localStorage.getItem(MIGRATION_FLAG)) return;

    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const eqIdx = cookie.indexOf('=');
      if (eqIdx === -1) continue;
      const name = cookie.substring(0, eqIdx).trim();
      const rawValue = cookie.substring(eqIdx + 1);

      const isAvmKey = ALL_PREFIXES.some((p) => name.startsWith(p));
      if (!isAvmKey) continue;

      try {
        const decoded = decodeURIComponent(rawValue);
        const entry: StoredEntry = { value: decoded, timestamp: Date.now() };
        localStorage.setItem(name, JSON.stringify(entry));
      } catch {
        // skip corrupt cookie
      }

      // Delete the cookie
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }

    localStorage.setItem(MIGRATION_FLAG, '1');
  } catch (e) {
    console.warn('[avm-local-storage] Migration error:', e);
    return;
  }

  // Run cleanup after migration to enforce limits
  cleanupAvmStorage();
}

// ---------------------------------------------------------------------------
// Public API — UI preferences (permanent, no TTL)
// ---------------------------------------------------------------------------

const VALUATION_VIEW_MODE_KEY = 'valuation_view_mode';

export function getValuationViewMode(): 'grid' | 'list' | null {
  if (!isBrowser()) return null;
  const v = localStorage.getItem(VALUATION_VIEW_MODE_KEY);
  if (v === 'grid' || v === 'list') return v;
  return null;
}

export function setValuationViewMode(mode: 'grid' | 'list'): void {
  if (!isBrowser()) return;
  localStorage.setItem(VALUATION_VIEW_MODE_KEY, mode);
}

const MY_LEADS_ONLY_KEY = 'valuation_my_leads_only';

export function getMyLeadsOnly(): boolean {
  if (!isBrowser()) return false;
  try {
    return localStorage.getItem(MY_LEADS_ONLY_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setMyLeadsOnly(value: boolean): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(MY_LEADS_ONLY_KEY, String(value));
  } catch {
    // Storage blocked: the toggle still works for this page, it just is not remembered.
  }
}

const UNREAD_ONLY_KEY = 'valuation_unread_only';

// KOQOON-297: persist the Pipeline "Unread" filter (like My Leads / chip filters)
// so it survives opening a lead and returning to the funnel view.
export function getUnreadOnly(): boolean {
  if (!isBrowser()) return false;
  return localStorage.getItem(UNREAD_ONLY_KEY) === 'true';
}

export function setUnreadOnly(value: boolean): void {
  if (!isBrowser()) return;
  localStorage.setItem(UNREAD_ONLY_KEY, String(value));
}
